import { splitWordsOnUpperCase } from "@repo/helpers";
import { Loading, RadioGroup, TextBox } from "@repo/ui";
import { useEffect, useMemo, useRef } from "react";

import type { MapType } from "./filterContext";

import { useMapSettings } from "../hooks";
import { EnrollmentFilter } from "./enrollmentFilter";
import { useEmptyFilters, useMapFilters } from "./filterContext";
import { GeographicViewFilters } from "./geographicViewFilters";
import { LiveViewMapFilters } from "./liveViewFilters";
import { RepresentativesFilters } from "./representativesFilter";

export function MapFilters() {
	const { filters, setFilters } = useMapFilters();
	const emptyMapFilter = useEmptyFilters();
	const mapSettingsQuery = useMapSettings();

	function renderFilters() {
		switch (filters.type) {
			case "LiveView":
				return <LiveViewMapFilters />;
			case "GeographicView":
				return <GeographicViewFilters />;
			case "Representatives":
				return <RepresentativesFilters filterType="Rep" />;
			case "Machines":
				return <RepresentativesFilters filterType="Machine" />;
			case "Enrollment":
				return <EnrollmentFilter />;
			default:
				return null;
		}
	}

	function handleLimitPositionsChange(val: string) {
		const parsedVal = parseInt(val);
		if (isNaN(parsedVal)) return;

		let lastPositions = val;
		if (parsedVal < 1) lastPositions = "1";
		else if (parsedVal > 3000) lastPositions = mapSettingsQuery.data?.maxPositions.toString();

		setFilters((prev) => (prev.lastPositions === lastPositions ? prev : { ...prev, lastPositions }));
	}

	const radioValues = useMemo(
		() => mapSettingsQuery.data?.mapTypes.map((type) => ({ id: type, value: splitWordsOnUpperCase(type) })) ?? [],
		[mapSettingsQuery.data?.mapTypes],
	);

	const defaultsAppliedRef = useRef(false);

	useEffect(() => {
		const settings = mapSettingsQuery.data;
		if (defaultsAppliedRef.current || !settings?.mapTypes?.length) return;
		defaultsAppliedRef.current = true;

		setFilters((prev) => ({
			...prev,
			leads: settings.showLeads,
			positions: settings.showPositions,
			showSales: settings.showSales,
			retailLocations: settings.showRetailLocations,
			lastPositions: settings.defaultMaxPositions.toString(),
			type: prev.type ?? (settings.mapTypes[0] as MapType),
		}));
	}, [mapSettingsQuery.data, setFilters]);

	return (
		<Loading loading={mapSettingsQuery.isLoading}>
			<Loading loading={filters.type == null}>
				<RadioGroup
					label="Views"
					value={filters.type}
					changeHandler={(val) => setFilters({ ...emptyMapFilter, type: val as MapType })}
					values={radioValues}
					hidden={mapSettingsQuery.data?.mapTypes?.length < 2}
				/>
			</Loading>
			{renderFilters()}
			<TextBox
				label="Limit to Last Number of Positions"
				type="number"
				inputMode="numeric"
				maxlength={4}
				value={filters.lastPositions}
				changeHandler={handleLimitPositionsChange}
			/>
			{filters.type === "LiveView" || filters.type === "GeographicView" ? (
				<span className="font-bold">
					Markers are colored by representative, if one representative, markers are colored by date.
				</span>
			) : null}
		</Loading>
	);
}
